/**
 * Browser detection and information utilities
 * This file provides comprehensive helpers for detecting browser type, OS, and capabilities
 */

/**
 * Detects browser and OS information from user agent.
 * @returns {Object} - Browser and OS information
 */
export const getBrowserAndOSInfo = () => {
    const userAgent = navigator?.userAgent || '';

    // Detect browser
    let browser = 'Unknown';
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        browser = 'Chrome';
    } else if (userAgent.includes('Firefox')) {
        browser = 'Firefox';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        browser = 'Safari';
    } else if (userAgent.includes('Edg')) {
        browser = 'Edge';
    } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
        browser = 'Opera';
    }

    // Detect OS
    let os = 'Unknown';
    if (userAgent.includes('Windows')) {
        os = 'Windows';
    } else if (userAgent.includes('Mac OS')) {
        os = 'macOS';
    } else if (userAgent.includes('Linux')) {
        os = 'Linux';
    } else if (userAgent.includes('Android')) {
        os = 'Android';
    } else if (
        userAgent.includes('iOS') ||
        userAgent.includes('iPhone') ||
        userAgent.includes('iPad')
    ) {
        os = 'iOS';
    }

    return { browser, os };
};

/**
 * Returns true if the user's browser is supported.
 * Checks for IE10 and below, and IE11.
 * @returns {boolean} - True if browser is supported
 */
export const checkBrowserSupport = () => {
    try {
        const ua = window?.navigator?.userAgent || '';
        // Check for IEv10 and below
        const isIE10orOlder = ua.indexOf('MSIE ') > 0;
        // Check for IEv11
        const isIE11 = ua.indexOf('Trident/') > 0;

        return !isIE10orOlder && !isIE11;
    } catch (error) {
        // If we can't detect, assume supported
        return true;
    }
};

/**
 * Bool indicating whether or not the user is using Safari.
 * @returns {boolean} - True if using Safari
 */
export const isSafari = () => {
    try {
        return /^((?!chrome|android).)*safari/i.test(navigator?.userAgent || '');
    } catch (error) {
        return false;
    }
};

/**
 * Gets the browser's preferred languages as an array
 * @returns {string[]} Array of language codes in order of preference
 */
export const getBrowserLanguages = () => {
    try {
        // navigator.languages is an array of preferred languages in order (Chrome, Firefox, Edge)
        // navigator.language is the primary language (fallback for Safari, IE)
        return navigator?.languages || [navigator?.language || navigator?.userLanguage || 'en'];
    } catch (error) {
        return ['en'];
    }
};

/**
 * Gets the browser's primary preferred language
 * @returns {string} The primary language code (e.g., 'en-US', 'es', 'fr')
 */
export const getPrimaryBrowserLanguage = () => {
    try {
        const languages = getBrowserLanguages();
        return languages?.[0] || 'en';
    } catch (error) {
        return 'en';
    }
};

/**
 * Gets the browser's primary preferred language without region code
 * @returns {string} The primary language without region (e.g., 'en', 'es', 'fr')
 */
export const getPrimaryBrowserLanguageBase = () => {
    try {
        const primaryLanguage = getPrimaryBrowserLanguage();
        // Split by dash and take the first part (language code without region)
        return primaryLanguage?.split('-')?.[0] || 'en';
    } catch (error) {
        return 'en';
    }
};

/**
 * Checks if the browser has a specific language in its preferences
 * @param {string} languageCode - The language code to check for (e.g., 'en', 'es')
 * @returns {boolean} True if the browser has this language in its preferences
 */
export const browserPrefersLanguage = (languageCode) => {
    try {
        if (!languageCode) return false;

        const lowerLanguageCode = languageCode.toLowerCase();
        return getBrowserLanguages().some(
            (lang) =>
                lang?.toLowerCase() === lowerLanguageCode ||
                lang?.toLowerCase()?.startsWith(lowerLanguageCode + '-'),
        );
    } catch (error) {
        return false;
    }
};

/**
 * Get debug information about browser language settings
 * Useful for troubleshooting language detection issues
 * @returns {Object} Object containing browser language information
 */
export const getBrowserLanguageDebugInfo = () => {
    try {
        return {
            preferredLanguages: getBrowserLanguages(),
            primaryLanguage: getPrimaryBrowserLanguage(),
            primaryLanguageBase: getPrimaryBrowserLanguageBase(),
            navigatorLanguageProperty: navigator?.language || null,
            navigatorLanguagesProperty: navigator?.languages || null,
            userAgentLanguage: navigator?.userLanguage || null,
        };
    } catch (error) {
        return {
            preferredLanguages: ['en'],
            primaryLanguage: 'en',
            primaryLanguageBase: 'en',
            navigatorLanguageProperty: null,
            navigatorLanguagesProperty: null,
            userAgentLanguage: null,
            error: error.message,
        };
    }
};

/**
 * Gets comprehensive browser information for analytics and debugging
 * @returns {Object} Complete browser environment information
 */
export const getComprehensiveBrowserInfo = () => {
    try {
        const { browser, os } = getBrowserAndOSInfo();

        return {
            browser,
            os,
            userAgent: navigator?.userAgent || '',
            language: getPrimaryBrowserLanguage(),
            languages: getBrowserLanguages(),
            isSupported: checkBrowserSupport(),
            isSafari: isSafari(),
            viewport: {
                width: window?.innerWidth || 0,
                height: window?.innerHeight || 0,
            },
            screen: {
                width: window?.screen?.width || 0,
                height: window?.screen?.height || 0,
            },
        };
    } catch (error) {
        return {
            browser: 'Unknown',
            os: 'Unknown',
            userAgent: '',
            language: 'en',
            languages: ['en'],
            isSupported: true,
            isSafari: false,
            viewport: { width: 0, height: 0 },
            screen: { width: 0, height: 0 },
            error: error.message,
        };
    }
};
